'use client'

import { useState, useEffect } from 'react'

/**
 * Minimum viewport height change to consider as a virtual keyboard.
 * Small changes (e.g., mobile address bar hiding) are ignored.
 */
const MIN_KEYBOARD_HEIGHT = 100

interface VirtualKeyboardState {
  keyboardHeight: number
  isKeyboardOpen: boolean
}

/**
 * Hook to detect the mobile virtual keyboard using the VisualViewport API.
 *
 * When the virtual keyboard opens on mobile, the visual viewport shrinks
 * while window.innerHeight stays the same. This hook calculates the
 * difference to determine keyboard height.
 *
 * Falls back gracefully on desktop or browsers without VisualViewport.
 *
 * @see Issue #4566 - Pin AI text entry bar above keyboard on mobile
 */
export function useVirtualKeyboard(enabled = true): VirtualKeyboardState {
  const [state, setState] = useState<VirtualKeyboardState>({
    keyboardHeight: 0,
    isKeyboardOpen: false,
  })

  useEffect(() => {
    if (!enabled) return
    const viewport = window.visualViewport
    if (!viewport) return

    const update = () => {
      const heightDiff = window.innerHeight - viewport.height
      const isOpen = heightDiff > MIN_KEYBOARD_HEIGHT
      setState({
        keyboardHeight: isOpen ? heightDiff : 0,
        isKeyboardOpen: isOpen,
      })
    }

    // Check initial state
    update()

    viewport.addEventListener('resize', update)
    viewport.addEventListener('scroll', update)

    return () => {
      viewport.removeEventListener('resize', update)
      viewport.removeEventListener('scroll', update)
    }
  }, [enabled])

  return state
}
